import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { signup } from '../../services/auth'
import { getAPIBaseURL } from '../../services/helpers'
import { getData } from '../../services/request'
import { validateEmail, validatePassword, validatePhoneNumber, validateAddress } from '../../services/validators'
import {
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  HomeModernIcon
} from '@heroicons/react/24/outline'
import Logo from '../../asset/images/logo.svg'

const CenterSignupPage = () => {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    organization: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    country: '',
    city: '',
    address: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState({
    organization: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    country: '',
    city: '',
    address: ''
  })
  const [generalError, setGeneralError] = useState('')
  const [countries, setCountries] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    fetchCountries()
  }, [])

  const fetchCountries = async () => {
    try {
      const url = `${getAPIBaseURL()}/v1/countries`
      const requestResult = await getData(url, {}, {})

      if (requestResult.error === '') {
        if (requestResult.result.status === 200) {
          setCountries(requestResult.result.data.data || [])
        }
      }
      setLoadingCountries(false)
    } catch (error) {
      setLoadingCountries(false)
    }
  }

  const onChange = (e) => {
    const { name, value } = e.target
    setForm({ ...form, [name]: value })
    // Clear error when user starts typing
    if (error[name]) {
      setError({ ...error, [name]: '' })
    }
  }

  const validateForm = () => {
    let isValid = true
    const newErrors = { ...error }

    // Validate organization
    if (!form.organization || form.organization.trim() === '') {
      newErrors.organization = 'Organization name is required'
      isValid = false
    } else if (form.organization.length < 2 || form.organization.length > 200) {
      newErrors.organization = 'Organization name must be between 2 to 200 characters'
      isValid = false
    }

    // Validate email
    const emailValidation = validateEmail(form.email)
    if (emailValidation.error !== '') {
      newErrors.email = emailValidation.result
      isValid = false
    }

    // Validate phone number
    const phoneValidation = validatePhoneNumber(form.phoneNumber)
    if (phoneValidation.error !== '') {
      newErrors.phoneNumber = phoneValidation.result
      isValid = false
    }

    // Validate password
    const passwordValidation = validatePassword(form.password)
    if (passwordValidation.error !== '') {
      newErrors.password = passwordValidation.result
      isValid = false
    }

    // Validate confirm password
    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
      isValid = false
    } else if (form.confirmPassword === '') {
      newErrors.confirmPassword = 'Please confirm your password'
      isValid = false
    }

    // Validate country
    if (!form.country || form.country.trim() === '') {
      newErrors.country = 'Country is required'
      isValid = false
    } else if (form.country.length !== 2) {
      newErrors.country = 'Country must be a valid 2-letter ISO code'
      isValid = false
    }

    // Validate city
    if (!form.city || form.city.trim() === '') {
      newErrors.city = 'City is required'
      isValid = false
    } else if (form.city.length < 2 || form.city.length > 100) {
      newErrors.city = 'City must be between 2 to 100 characters'
      isValid = false
    }

    // Validate address
    const addressValidation = validateAddress(form.address)
    if (addressValidation.error !== '') {
      newErrors.address = addressValidation.result
      isValid = false
    }

    setError(newErrors)
    return isValid
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setGeneralError('')

    if (!validateForm()) {
      return
    }

    setLoading(true)
    const payload = {
      role: 'CENTER',
      organization: form.organization,
      email: form.email,
      phoneNumber: form.phoneNumber,
      password: form.password,
      country: form.country,
      city: form.city,
      address: form.address
    }

    const res = await signup(payload)
    setLoading(false)

    if (res.status === 201 || res.status === 200) {
      navigate('/center/login?verify=1')
    } else {
      setGeneralError(res.error || 'Signup failed')
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gradient-to-br from-stone-50 via-amber-50/30 to-emerald-50/40 font-sans">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center items-center gap-1.5 mb-8 group">
          <img
            src={Logo}
            alt="Obule"
            className="h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
          />
          <span className="text-3xl font-semibold tracking-wide text-slate-900">
            bule
          </span>
        </Link>
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900">
            Partner with Obule
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Create an account to manage your event center
          </p>
        </div>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white px-6 py-10 shadow-xl border border-slate-100 sm:rounded-2xl sm:px-12">
          <form className="space-y-6" onSubmit={onSubmit}>
            {generalError && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-100 flex items-start gap-3 text-red-700 font-medium text-sm">
                <p>{generalError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold leading-6 text-slate-900">Organization Name</label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <BuildingOfficeIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </div>
                  <input
                    name="organization"
                    value={form.organization}
                    onChange={onChange}
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 transition-all"
                    placeholder="Enter your organization name"
                  />
                </div>
                {error.organization && <p className="mt-1 text-sm text-red-600">{error.organization}</p>}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold leading-6 text-slate-900">Email Address</label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <EnvelopeIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </div>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 transition-all"
                    placeholder="name@example.com"
                  />
                </div>
                {error.email && <p className="mt-1 text-sm text-red-600">{error.email}</p>}
              </div>

              <div className="sm:col-span-1">
                <label className="block text-sm font-semibold leading-6 text-slate-900">Phone Number</label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <PhoneIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </div>
                  <input
                    name="phoneNumber"
                    type="tel"
                    value={form.phoneNumber}
                    onChange={onChange}
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 transition-all"
                    placeholder="08012345678"
                  />
                </div>
                {error.phoneNumber && <p className="mt-1 text-sm text-red-600">{error.phoneNumber}</p>}
              </div>

              <div className="sm:col-span-1 hidden sm:block">
                {/* Spacer to keep grid aligned */}
              </div>

              <div className="sm:col-span-1">
                <label className="block text-sm font-semibold leading-6 text-slate-900">Country</label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MapPinIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </div>
                  <select
                    name="country"
                    value={form.country}
                    onChange={onChange}
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 focus:outline-none bg-white transition-all"
                  >
                    <option value="">{loadingCountries ? 'Loading...' : 'Select Country'}</option>
                    {!loadingCountries && countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                {error.country && <p className="mt-1 text-sm text-red-600">{error.country}</p>}
              </div>

              <div className="sm:col-span-1">
                <label className="block text-sm font-semibold leading-6 text-slate-900">City</label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <HomeModernIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </div>
                  <input
                    name="city"
                    type="text"
                    value={form.city}
                    onChange={onChange}
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 transition-all"
                    placeholder="e.g. Lagos"
                  />
                </div>
                {error.city && <p className="mt-1 text-sm text-red-600">{error.city}</p>}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold leading-6 text-slate-900">Full Address</label>
                <div className="relative mt-2">
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={onChange}
                    rows="3"
                    className="block w-full rounded-lg border-0 py-2.5 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 transition-all resize-none"
                    placeholder="Enter your organization's full address"
                  />
                </div>
                {error.address && <p className="mt-1 text-sm text-red-600">{error.address}</p>}
              </div>

              <div className="sm:col-span-1">
                <label className="block text-sm font-semibold leading-6 text-slate-900">Password</label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <LockClosedIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </div>
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={onChange}
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 transition-all"
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <EyeIcon className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
                {error.password && <p className="mt-1 text-sm text-red-600">{error.password}</p>}
              </div>

              <div className="sm:col-span-1">
                <label className="block text-sm font-semibold leading-6 text-slate-900">Confirm Password</label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <LockClosedIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </div>
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={form.confirmPassword}
                    onChange={onChange}
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6 transition-all"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <EyeIcon className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
                {error.confirmPassword && <p className="mt-1 text-sm text-red-600">{error.confirmPassword}</p>}
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center rounded-lg px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 transition-all disabled:opacity-70 disabled:cursor-not-allowed hover:shadow-md hover:-translate-y-0.5 transform duration-200"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>

            <div className="text-center text-sm text-slate-500">
              Already have an account?{' '}
              <Link to="/center/login" className="font-semibold text-emerald-600 hover:text-emerald-500">
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CenterSignupPage
